import http, { endpoints } from '../http';
import { LocalizedError } from '@libs/localization';
import { mapActivityFromDto } from '../activities/helpers';
import { mapContactFromDto } from '../contacts/helpers';
import { mapCaseOrderFromDeliveryDto, mapOrderBookingFromDto, mapOrderContactFromDto, mapOrderFromDto, mapOrderLineFromDto, mapPhotographerInstructionFromDto, mapSubmitFeedbackParamsToDto, mapUpdateOrderAssignmentParamsToDto, mapUpdateOrderParamsToDto, } from './helpers';
const efktClient = http.createClient(endpoints.efkt);
export default class OrdersApi {
    /**
     * `POST /api/order` — the whole wizard in one call: creates the local order, the CRM sales order,
     * and confirms it for production. Replaces the old `createOrder` → `setBundle` → `replaceAddons`
     * → `updatePendingOrder` → `confirmOrder` chain; each section below is one of those calls' body,
     * unchanged, just nested.
     *
     * The path used to be `/api/order/submit` (AXIT-567). `POST /api/order` was the per-step create
     * route, which lost its last caller when the wizard moved to one submit and was deleted; this
     * route took its path (AXIT-576). The remaining per-step routes stay available and unchanged.
     *
     * `id` is a client-minted idempotency key **and** the order's primary key — dedup falls out of
     * the database's own uniqueness constraint. Keep it stable across retries; a fresh id per attempt
     * is exactly as exposed to double-create as sending none.
     *
     * Not transactional — the writes span CRM and the Efkt database, so there is no rollback on
     * failure. What it guarantees instead is convergence: every step is idempotent or a full
     * replace, so **re-submitting the identical payload is the recovery path**, not "clean up and
     * start over". A failure after the first write step leaves the order at `Pending`, still
     * editable, still keyed on this same `id`.
     *
     * Error map worth handling at the call site: 409 the id describes a different account/reference
     * (or a concurrent CRM webhook already materialised the order under its own id) — the key is
     * dead, a new draft is needed; 422 `accountBlacklistedFromOrderCreation` — branch blocked at the
     * gateway, the Sales API never called; 422 with `problems[]` — the selection cannot be produced,
     * raised by the pre-flight before the first write, so **nothing exists and the `id` is unspent**
     * (see `getConfirmOrderProblems`); 503 — a transient CRM/catalogue read, or the package's CRM
     * member lines not landing inside the backend's 10s wait, so retrying the identical payload is
     * the fix; 429 — CRM rejection, empty body, not a rate limit, retrying unchanged cannot help;
     * 502/500 — CRM transport / local write failure, retry or support.
     */
    static async submitOrder({ id, accountId, reference, grouping, bundleId, addons, details, }) {
        const body = {
            id,
            accountId,
            reference,
            grouping,
            bundle: bundleId ? { id: bundleId } : null,
            addons,
            details: mapUpdateOrderParamsToDto(details),
        };
        const response = await efktClient.post('/api/order', body);
        return {
            orderId: response.data.orderId,
            crmOrderId: response.data.crmOrderId,
        };
    }
    static async getOrderById(orderId) {
        const response = await efktClient.get(`/api/order/${encodeURIComponent(orderId)}`);
        return mapOrderFromDto(response.data);
    }
    static async getOrderLines(orderId) {
        const response = await efktClient.get(`/api/order/${encodeURIComponent(orderId)}/order-lines`, { params: { limit: 100 } });
        return response.data.items.map(mapOrderLineFromDto);
    }
    /**
     * `GET /api/case/{caseId}/deliveries`, top level only (no `includeAssets`) — there is no
     * gateway route that filters `/api/orders` by case, so the case's order list has to come from
     * here. Mirrors datahub's `OrdersApi.getCaseOrders`, which also feeds `CaseOrders`'s
     * `orders: ISimplifiedOrder[]` prop; see `mapCaseOrderFromDeliveryDto` for what is and isn't
     * filled in on each row.
     */
    static async getCaseOrders(caseId) {
        const response = await efktClient.get(`/api/case/${encodeURIComponent(caseId)}/deliveries`);
        return response.data.items.map(dto => mapCaseOrderFromDeliveryDto(dto, caseId));
    }
    /** `GET /api/orders/{orderId}/contacts` — bare array, each row carrying its role(s) on the order. */
    static async getOrderContacts(orderId) {
        const response = await efktClient.get(`/api/orders/${encodeURIComponent(orderId)}/contacts`);
        return response.data.map(mapOrderContactFromDto);
    }
    /**
     * `GET /api/orders/{orderId}/responsible-agents` — bare array of the same contact-card shape
     * `ContactsApi.listContacts` returns, so this reuses that mapper rather than a new one.
     */
    static async getResponsibleAgents(orderId) {
        const response = await efktClient.get(`/api/orders/${encodeURIComponent(orderId)}/responsible-agents`);
        return response.data.map(mapContactFromDto);
    }
    /** `GET /api/orders/{orderId}/notification-recipients` — the order's watchers, same shape as above. */
    static async getOrderWatchers(orderId) {
        const response = await efktClient.get(`/api/orders/${encodeURIComponent(orderId)}/notification-recipients`);
        return response.data.map(mapContactFromDto);
    }
    /**
     * `GET /api/order/{orderId}/photographer-instruction`. Distinct from `updatePendingOrder`'s
     * `photographerNotes` field — this is a separate CC/Booking instruction, read-modelled with no
     * author or timestamp. `content` absent means none is set, not an error.
     */
    static async getPhotographerInstruction(orderId) {
        const response = await efktClient.get(`/api/order/${encodeURIComponent(orderId)}/photographer-instruction`);
        return mapPhotographerInstructionFromDto(response.data);
    }
    /** `PUT /api/order/{orderId}/photographer-instruction`. Blank or omitted content retires it. */
    static async setPhotographerInstruction(orderId, content) {
        const body = { content };
        await efktClient.put(`/api/order/${encodeURIComponent(orderId)}/photographer-instruction`, body);
    }
    /**
     * `GET /api/order/{orderId}/bookings` — working today, read-only
     * (BOOQLY-DUAL-API-INVESTIGATION.md §14.7). This screen shows a single booking per order, so only
     * the first page's first row is read; a second, later booking on the same order is not shown.
     */
    static async getOrderBookings(orderId) {
        const response = await efktClient.get(`/api/order/${encodeURIComponent(orderId)}/bookings`);
        return response.data.items.map(mapOrderBookingFromDto);
    }
    /**
     * `PUT /api/order/{orderId}`, the responsible-agent / watchers half only — see
     * `mapUpdateOrderAssignmentParamsToDto`. The details page's two independent selects (Responsible
     * agent, Watchers) each call this with only the field that changed.
     */
    static async updateOrderAssignment(orderId, params) {
        await efktClient.put(`/api/order/${encodeURIComponent(orderId)}`, mapUpdateOrderAssignmentParamsToDto(params));
    }
    /**
     * `POST /api/order/{orderId}/orderlines/add` — EFKT-MOCK, no gateway route exists yet. Mirrors
     * datahub's `OrdersApi.addOrderLines` exactly: same params shape (so the shared
     * `CaseAddExtraOrderLineModal` needs no per-source branching beyond which `OrdersApi` it calls),
     * same per-line `{success}` response, same "any line failed ⇒ throw" behaviour. Adds lines to an
     * existing order — it does not create one.
     */
    static async addOrderLines(params) {
        const body = params.caseProducts.map(({ itemNumber, config, quantity, variant }) => ({
            itemId: itemNumber !== null && itemNumber !== void 0 ? itemNumber : '',
            config,
            quantity,
            variant: variant !== null && variant !== void 0 ? variant : '',
        }));
        const response = await efktClient.post(`/api/order/${encodeURIComponent(params.orderId)}/orderlines/add`, body);
        if (response.data.some(({ success }) => !success)) {
            throw new LocalizedError('modals.addExtraOrderLine.orderUpdateError');
        }
    }
    /** `GET /api/order/{orderId}/activities` — `{items, total}`, newest first. No booqly consumer yet. */
    static async getOrderActivities(orderId, page = 1, limit = 20) {
        const response = await efktClient.get(`/api/order/${encodeURIComponent(orderId)}/activities`, { params: { page, limit } });
        return {
            items: response.data.items.map(mapActivityFromDto),
            total: response.data.total,
        };
    }
    /**
     * EFKT-BE-GAP — **the route does not exist yet, and this calls it anyway. That is the point.**
     *
     * No feedback/complaint capability exists anywhere in Efkt: not on the fe-gateway
     * (`Efkt/Applications/Gateway/Frontend/Endpoints/**`), not in `Efkt/Applications/Api/**`, not in
     * `Efkt/Contracts` or `libs/Efkt.Contracts`. Verified by grep, not assumed from silence.
     *
     * `POST /api/order-line/{orderLineId}/feedback` is our assumed shape — order-line-anchored to match
     * the one real route family this domain already has (`/api/order-line/{id}/assets`,
     * `/api/order-line/{id}/internal-notes`), with a body mirroring datahub's `IFeedbackParamsDto`
     * field for field. See `mapSubmitFeedbackParamsToDto`.
     *
     * **Do not make this fall back to datahub.** Once the feedback page resolves an order line as
     * Efkt-owned, this is the call it makes; the row does not exist in datahub, so writing there would
     * put a complaint on the wrong backend. It 404s today, `FeedbackPageContent` catches that and shows
     * `cases.pages.feedback.error`, and the 404 in the network tab is the signal that the BE work is
     * still outstanding. When BE ships the route, verify the contract against this shape — there should
     * be nothing to rewire.
     */
    static async submitFeedback(params) {
        await efktClient.post(`/api/order-line/${encodeURIComponent(params.orderLineId)}/feedback`, mapSubmitFeedbackParamsToDto(params));
    }
}
